
import { useState, useEffect, useCallback } from 'react';
import { ALL_ALERTS } from '../constants/alertDefinitions';

const ACKS_KEY = 'tracking_app_acked_alerts';
const LATCH_KEY = 'tracking_app_latched_alerts';

export const useAlertEngine = (devices) => {
    const [activeAlerts, setActiveAlerts] = useState([]);

    // Persistent State: ACKs
    const [ackedMap, setAckedMap] = useState(() => {
        try {
            const stored = localStorage.getItem(ACKS_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch (e) {
            console.error('Failed to parse acked alerts', e);
            return {};
        }
    });

    // Persistent State: Latched Alerts (for Sticky logic)
    // Stores timestamp of when the alert was first latched/triggered
    const [latchedMap, setLatchedMap] = useState(() => {
        try {
            const stored = localStorage.getItem(LATCH_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch (e) {
            console.error('Failed to parse latched alerts', e);
            return {};
        }
    });

    // Sync to Storage
    useEffect(() => {
        localStorage.setItem(ACKS_KEY, JSON.stringify(ackedMap));
    }, [ackedMap]);

    useEffect(() => {
        localStorage.setItem(LATCH_KEY, JSON.stringify(latchedMap));
    }, [latchedMap]);

    // Action: Acknowledge an alert
    const ackAlert = useCallback((deviceId, definitionId) => {
        const key = `${definitionId}:${deviceId}`;
        // Add to Acked (So it stops showing)
        setAckedMap(prev => ({ ...prev, [key]: Date.now() }));
        // Remove from Latched (Clear the sticky state)
        setLatchedMap(prev => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    // Action: Clear ACK (reset state) - e.g., if manually resetting status
    const clearAck = useCallback((deviceId, definitionId) => {
        const key = `${definitionId}:${deviceId}`;
        setAckedMap(prev => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    // Evaluation Loop
    useEffect(() => {
        const nextAlerts = [];
        const newLatches = {};
        const unAckKeys = [];
        const removeLatchKeys = []; // stale latches on non-sticky alerts

        devices.forEach(device => {
            const devId = device.deviceId || device.id;
            if (!devId) return;

            ALL_ALERTS.forEach(def => {
                const key = `${def.id}:${devId}`;
                const isConditionMet = def.predicate(device);
                const isLatched = !!latchedMap[key];
                const isAcked = !!ackedMap[key];

                // Non-sticky alerts should never hold a latch; clean up any stale one
                // (e.g. left over from when OK_ALERT_DEF was sticky)
                if (!def.isSticky && isLatched) {
                    removeLatchKeys.push(key);
                }

                // 1. Latching Logic — sticky alerts only
                if (def.isSticky && isConditionMet && !isLatched) {
                    newLatches[key] = Date.now();
                }

                // 2. Visibility Logic
                // Sticky: visible while condition OR latch is active, until acked
                // Non-sticky: visible only while condition is active, until acked
                const effectivelyLatched = def.isSticky && (isLatched || !!newLatches[key]);
                const forceShow = effectivelyLatched; // only sticky can force-show past condition=false

                const shouldShow = (isConditionMet || forceShow) && !isAcked;

                if (shouldShow) {
                    nextAlerts.push({
                        id: `${def.id}-${devId}`,
                        definitionId: def.id,
                        device: device,
                        message: def.messageBuilder(device),
                        severity: def.severity,
                        timestamp: latchedMap[key] || Date.now()
                    });
                }

                // 3. Auto-Clear Ack so the alert re-fires next time the condition triggers
                // Sticky: clear ack when condition is false AND latch is gone (user acked the latch)
                // Non-sticky: clear ack whenever condition is false (latch state irrelevant)
                const shouldClearAck = !isConditionMet && isAcked &&
                    (!def.isSticky || !effectivelyLatched);
                if (shouldClearAck) {
                    unAckKeys.push(key);
                }

                // DEBUG: Log if SOS seems active but hidden
                if (def.id === 'SOS' && (isConditionMet || isLatched)) {
                    console.log(`[AlertEngine] SOS Eval: Device=${devId}`, {
                        isConditionMet,
                        isLatched,
                        isAcked,
                        effectivelyLatched,
                        shouldShow
                    });
                }
            });
        });

        // Batch Updates to avoid render loops
        if (Object.keys(newLatches).length > 0) {
            setLatchedMap(prev => ({ ...prev, ...newLatches }));
        }

        if (removeLatchKeys.length > 0) {
            setLatchedMap(prev => {
                const next = { ...prev };
                removeLatchKeys.forEach(k => delete next[k]);
                return next;
            });
        }

        if (unAckKeys.length > 0) {
            setAckedMap(prev => {
                const next = { ...prev };
                unAckKeys.forEach(k => delete next[k]);
                return next;
            });
        }

        // Sort active alerts by priority
        nextAlerts.sort((a, b) => {
            const pA = ALL_ALERTS.find(d => d.id === a.definitionId)?.priority || 0;
            const pB = ALL_ALERTS.find(d => d.id === b.definitionId)?.priority || 0;
            return pB - pA;
        });

        // Naive structural comparison to avoid re-render loops if data hasn't changed meaningfully
        setActiveAlerts(prev => {
            if (prev.length !== nextAlerts.length) return nextAlerts;
            const diff = prev.some((p, i) => p.id !== nextAlerts[i].id);
            return diff ? nextAlerts : prev;
        });

    }, [devices, ackedMap, latchedMap]);

    return {
        activeAlerts,
        ackAlert,
        clearAck,
        isAcked: (defId, devId) => !!ackedMap[`${defId}:${devId}`]
    };
};
